/**
 * Tyndo time + mode contexts.
 *
 * Two cross-cutting concerns shared by every operational module:
 *
 *   1. **Mode** — Now / History / Explore. Dictates whether the UI subscribes
 *      to live data, time-travels, or runs filtered queries.
 *   2. **Time range** — the scrubber state. "Now" with a relative window by
 *      default; switches to a frozen [from, to] when user time-travels.
 *
 * Both are wrapped in lightweight React Contexts so any module can opt-in
 * via `useMode()` / `useTimeRange()` without bringing the full shell
 * dependency chain. Defaults are safe (no-op) when not provided by the shell.
 *
 * Persistence: mode/range are persisted to localStorage keyed by `scope`
 * (typically a moduleId). Lets each module remember its own user preference.
 */

import * as React from 'react';

// ─── Types ───────────────────────────────────────────────────────────────

export type TyndoMode = 'now' | 'history' | 'explore';

export type TyndoTimeRange =
  | { kind: 'relative'; window: '1h' | '8h' | '24h' | '7d' | 'shift' }
  | { kind: 'absolute'; from: string; to: string };

interface ModeContextValue {
  mode: TyndoMode;
  setMode: (mode: TyndoMode) => void;
}

interface TimeRangeContextValue {
  range: TyndoTimeRange;
  setRange: (range: TyndoTimeRange) => void;
  /** True when range is relative AND ends "now" — drives live subscriptions. */
  isLive: boolean;
}

const ModeContext = React.createContext<ModeContextValue>({
  mode: 'now',
  setMode: () => {
    // Default context value: no <ModeProvider> mounted, mode changes are ignored.
  },
});

const TimeRangeContext = React.createContext<TimeRangeContextValue>({
  range: { kind: 'relative', window: '8h' },
  setRange: () => {
    // Default context value: no <TimeRangeProvider> mounted, range changes are ignored.
  },
  isLive: true,
});

// ─── Providers ──────────────────────────────────────────────────────────

interface ModeProviderProps {
  /** Persistence scope key — typically a moduleId. */
  scope?: string;
  defaultMode?: TyndoMode;
  children: React.ReactNode;
}

export function ModeProvider({
  scope = 'global',
  defaultMode = 'now',
  children,
}: ModeProviderProps) {
  const [mode, setModeState] = React.useState<TyndoMode>(() => {
    if (typeof window === 'undefined') return defaultMode;
    try {
      const stored = window.localStorage.getItem(`tyndo.mode.${scope}`);
      return (stored as TyndoMode) ?? defaultMode;
    } catch {
      return defaultMode;
    }
  });

  const setMode = React.useCallback(
    (next: TyndoMode) => {
      setModeState(next);
      try {
        window.localStorage.setItem(`tyndo.mode.${scope}`, next);
      } catch {
        /* ignore */
      }
    },
    [scope]
  );

  const value = React.useMemo(() => ({ mode, setMode }), [mode, setMode]);
  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

interface TimeRangeProviderProps {
  scope?: string;
  defaultRange?: TyndoTimeRange;
  children: React.ReactNode;
}

export function TimeRangeProvider({
  scope = 'global',
  defaultRange = { kind: 'relative', window: '8h' },
  children,
}: TimeRangeProviderProps) {
  const [range, setRangeState] = React.useState<TyndoTimeRange>(() => {
    if (typeof window === 'undefined') return defaultRange;
    try {
      const stored = window.localStorage.getItem(`tyndo.range.${scope}`);
      return stored ? (JSON.parse(stored) as TyndoTimeRange) : defaultRange;
    } catch {
      return defaultRange;
    }
  });

  const setRange = React.useCallback(
    (next: TyndoTimeRange) => {
      setRangeState(next);
      try {
        window.localStorage.setItem(`tyndo.range.${scope}`, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [scope]
  );

  const isLive = range.kind === 'relative';

  const value = React.useMemo(
    () => ({ range, setRange, isLive }),
    [range, setRange, isLive]
  );

  return (
    <TimeRangeContext.Provider value={value}>
      {children}
    </TimeRangeContext.Provider>
  );
}

// ─── Hooks ───────────────────────────────────────────────────────────────

export function useMode() {
  return React.useContext(ModeContext);
}

export function useTimeRange() {
  return React.useContext(TimeRangeContext);
}

export { ModeContext, TimeRangeContext };
